"use client";

import { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Item,
  ItemContent,
  ItemActions,
  ItemTitle,
} from "@/components/ui/item";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ItemsListCardDialog,
  type ItemsListCardFormApi,
  type ItemsListCardSchema,
} from "@/components/backoffice/items-list-card-dialog";
import { PlusIcon, SquarePenIcon, Trash2Icon } from "lucide-react";
import { Badge } from "@/components/ui/badge";

type BaseItem = {
  id: number;
  isActive: boolean;
};

interface ItemsListCardProps<T extends BaseItem, TCreate> {
  title: string;
  items: T[];
  createSchema: ItemsListCardSchema;
  editSchema: ItemsListCardSchema;
  defaultCreateValues: TCreate;

  renderItem: (item: T) => React.ReactNode;
  renderFormFields: (form: ItemsListCardFormApi<TCreate>) => React.ReactNode;

  onCreate(data: TCreate): Promise<void>;
  onUpdate(data: T): Promise<void>;
  onDelete(id: { id: number }): Promise<void>;
}

function ItemsListCard<T extends BaseItem, TCreate = Omit<T, "id">>({
  title,
  items,
  createSchema,
  editSchema,
  defaultCreateValues,
  renderItem,
  renderFormFields,
  onCreate,
  onUpdate,
  onDelete,
}: ItemsListCardProps<T, TCreate>) {
  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);

  const openCreate = () => {
    setEditingItem(null);
    setOpen(true);
  };

  const openEdit = (item: T) => {
    setEditingItem(item);
    setOpen(true);
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{title}</CardTitle>
          <Button onClick={openCreate}>
            <PlusIcon />
          </Button>
        </CardHeader>

        <CardContent className="space-y-4">
          {items.map((item) => (
            <Item key={item.id} variant="muted">
              {renderItem(item)}
              <ItemActions>
                <Badge
                  variant={item.isActive ? "default" : "outline"}
                  onClick={() =>
                    onUpdate({ ...item, isActive: !item.isActive })
                  }
                >
                  {item.isActive ? "Actif" : "Inactif"}
                </Badge>
                <ButtonGroup>
                  <Button onClick={() => openEdit(item)}>
                    <SquarePenIcon />
                  </Button>

                  <Button
                    variant="destructive"
                    onClick={() => onDelete({ id: item.id })}
                  >
                    <Trash2Icon />
                  </Button>
                </ButtonGroup>
              </ItemActions>
            </Item>
          ))}
        </CardContent>
      </Card>

      <ItemsListCardDialog
        open={open}
        onOpenChange={setOpen}
        item={editingItem}
        createSchema={createSchema}
        editSchema={editSchema}
        defaultCreateValues={defaultCreateValues}
        renderFormFields={renderFormFields}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />
    </>
  );
}

function ItemsListCardSkeleton() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Skeleton className="h-4 w-[200px]" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((item, index) => (
            <Item key={index}>
              <ItemContent>
                <ItemTitle>
                  <Skeleton className="h-4 w-[200px]" />
                </ItemTitle>
              </ItemContent>
              <ItemActions>
                <Skeleton className="h-4 w-[50px]" />
              </ItemActions>
            </Item>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export { ItemsListCard, ItemsListCardSkeleton };
